/**
 * Keeps a playing wall tile on the shared timeline without visible jumps
 * (checked about twice a second while the tile plays).
 *   |error| <= 40 ms (about a frame): leave it, normal speed
 *   40–250 ms: nudge the playback rate by 3% until it's back (muted followers only)
 *   > 250 ms on two readings in a row: hard-seek (aimed ahead by the seek time)
 */
export const WALL_DRIFT_IGNORE_SECONDS = 0.04;
export const WALL_DRIFT_SEEK_SECONDS = 0.25;
export const WALL_DRIFT_RATE_NUDGE = 0.03;
export const WALL_DRIFT_CHECK_MS = 500;
/** Seek-loop guard: at most one hard seek per tile in this window. */
export const WALL_DRIFT_MIN_SEEK_INTERVAL_MS = 5_000;
/**
 * A seek on Tizen lands on the keyframe at or before the target. If the last seek landed
 * this far off (or more), seeking again would land in the same place, so don't.
 */
export const WALL_DRIFT_SEEK_LANDING_TOLERANCE_SECONDS = 0.1;
/** currentTime − timeline position, folded across the loop point (e.g. 29.9 s vs 0.1 s is −0.2 s). */
export function wallDriftErrorSeconds(currentSeconds, wantSeconds, durationSeconds) {
    let error = currentSeconds - wantSeconds;
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
        if (error > durationSeconds / 2)
            error -= durationSeconds;
        if (error < -durationSeconds / 2)
            error += durationSeconds;
    }
    return error;
}
export function wallDriftAction(errorSeconds, previousErrorSeconds, options = { canNudgeRate: true }) {
    const size = Math.abs(errorSeconds);
    if (size > WALL_DRIFT_SEEK_SECONDS) {
        // One odd reading isn't enough to justify a visible jump, and the seek-loop guard
        // (canSeek false) means the rest is closed with the rate nudge instead.
        if (options.canSeek !== false &&
            previousErrorSeconds !== null &&
            Math.abs(previousErrorSeconds) > WALL_DRIFT_SEEK_SECONDS) {
            return { kind: 'seek' };
        }
        if (!options.canNudgeRate)
            return { kind: 'rate', rate: 1 };
        return { kind: 'rate', rate: errorSeconds > 0 ? 1 - WALL_DRIFT_RATE_NUDGE : 1 + WALL_DRIFT_RATE_NUDGE };
    }
    // The (0,0) tile has the sound: changing its speed would change the pitch, so it
    // stays at normal speed and is the reference the muted followers adjust to.
    if (!options.canNudgeRate)
        return { kind: 'rate', rate: 1 };
    if (size > WALL_DRIFT_IGNORE_SECONDS) {
        // Ahead (positive error) → slow down; behind → speed up.
        return { kind: 'rate', rate: errorSeconds > 0 ? 1 - WALL_DRIFT_RATE_NUDGE : 1 + WALL_DRIFT_RATE_NUDGE };
    }
    return { kind: 'rate', rate: 1 };
}
//# sourceMappingURL=wall-drift.js.map